import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import ProgressBar from '@atlassian/jira-common-components-progress-bar/src/main.tsx';
import ProgressElement from '@atlassian/jira-common-components-progress-bar/src/progress-element/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { Container } from '@atlassian/jira-version-modals/src/common/components/text-cell/styled.tsx';
import type { progressBar_projectVersions_VersionProgress$key } from './__generated__/progressBar_projectVersions_VersionProgress.graphql';
import messages from './messages.tsx';

export type Props = {
	query: progressBar_projectVersions_VersionProgress$key;
};

const VersionProgress = ({ query }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const progress = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment progressBar_projectVersions_VersionProgress on JiraVersion {
				done: issues(filter: DONE) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
				inProgress: issues(filter: IN_PROGRESS) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
				toDo: issues(filter: TODO) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
			}
		`,
		query,
	);

	const doneCount = progress.done.totalCount;
	const inProgressCount = progress.inProgress.totalCount;
	const toDoCount = progress.toDo.totalCount;

	return toDoCount || inProgressCount || doneCount ? (
		<BarContainer>
			<ProgressBar
				sum={doneCount + inProgressCount + toDoCount}
				remainingTooltipContent={formatMessage(messages.toDo, {
					toDo: toDoCount,
				})}
				backgroundColor={token('color.border')}
			>
				<ProgressElement
					value={doneCount}
					tooltipContent={formatMessage(messages.done, {
						done: doneCount,
					})}
					color={token('color.border.success')}
				/>
				<ProgressElement
					value={inProgressCount}
					tooltipContent={formatMessage(messages.inProgress, {
						inProgress: inProgressCount,
					})}
					color={token('color.border.information')}
				/>
			</ProgressBar>
		</BarContainer>
	) : (
		<TextContainer>
			{formatMessage(
				fg('jira-issue-terminology-refresh-m3') ? messages.emptyIssueTermRefresh : messages.empty,
			)}
		</TextContainer>
	);
};

const TextContainer = Container;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BarContainer = styled(Container)({
	paddingTop: '13.2px',
});

export default VersionProgress;
